/**
 * @file
 * A library to log user events and properties to an arbitrary analytics tool.
 */

import OkC from "shared/util/OkcApi";
import ClientStats from "shared/util/ClientStats";
import EVENT_NAMES from "analytics/util/EventNames";
import CUSTOM_EVENT_TYPES from "analytics/util/CustomEventTypes";
import isApp from "shared/util/isApp";


// consts
// ----------------------------------------------------------------------------

// mparticle functions
const _funcs = {
	logEvent: "logEvent",
	setUserAttributeList: "setUserAttributeList",
	setUserAttribute: "setUserAttribute",
	removeUserAttribute: "removeUserAttribute",
	identify: "identify",
	getAllUserAttributes: "getAllUserAttributes",
};

// mparticle functions that must be run on a user object, not the bare
// mparticle object
const _userFuncs = {
	setUserTag: "setUserTag",
	removeUserTag: "removeUserTag",
	setUserAttribute: "setUserAttribute",
	removeUserAttribute: "removeUserAttribute",
	setUserAttributeList: "setUserAttributeList",
	removeAllUserAttributes: "removeAllUserAttributes",
	getUserAttributesLists: "getUserAttributesLists",
	getAllUserAttributes: "getAllUserAttributes",
};


// keep track of a buffer of events populated when mparticle is in the process
// of logging in or signging out.
// ----------------------------------------------------------------------------

let _sessionChangeEventQueue = [];

function _addToLoggingInEventQueue(func, args) {
	return new Promise((resolve) => {
		_sessionChangeEventQueue.push({ func, args, resolve });
	});
}


// general analytics functions
// ----------------------------------------------------------------------------

function _call(func, args) {
	if (window.Context.get("debugAnalytics")) {
		const user = window.mParticle.Identity.getCurrentUser();
		let mparticleUserid;

		if (user && user.getUserIdentities) {
			const userIdentities = user.getUserIdentities();

			if (userIdentities && userIdentities.userIdentities) {
				mparticleUserid = userIdentities.userIdentities.customerid;
			}
		}

		console.log(
			`ANALYTICS (userid ${mparticleUserid}) ${func}:`,
			JSON.stringify(args, null, 2),
		);
	}

	if (func === _funcs.identify) {
		if (!window.mParticle.Identity) {
			return false;
		}

		return window.mParticle.Identity.identify(args);
	} else if (_userFuncs[func]) {
		if (!window.mParticle.Identity || !window.mParticle.Identity.getCurrentUser) {
			return false;
		}

		const user = window.mParticle.Identity.getCurrentUser();

		if (!user) {
			return false;
		}

		return user[func](...args);
	} else {
		return window.mParticle[func](...args);
	}
}

function _callSoon(func, args) {
	return new Promise((resolve) => {
		if (window.mParticle) {
			if (window.mParticleIsLoaded && window.mParticleIsChangingSession) {
				resolve(_addToLoggingInEventQueue(func, args));
			} else if (window.mParticleIsLoaded) {
				resolve(_call(func, args));
			} else {
				window.mParticle.ready(() => {
					resolve(_call(func, args));
				});
			}
		}
	});
}

/**
 * Run any events that were queued up during mparticle login or sign out, then
 * clear the queue.
 */
function onChangeSession() {
	if (window.Context.get("debugAnalytics")) {
		console.log("ANALYTICS running login/logout callback");
	}

	window.mParticleIsChangingSession = false;

	_sessionChangeEventQueue.forEach(({ func, args, resolve }) => {
		resolve(_call(func, args));
	});

	_sessionChangeEventQueue = [];
}

export function processName(obj) {
	if (typeof obj === "string") {
		return obj.toLowerCase();
	}

	if (typeof obj === "boolean") {
		return obj ? "true" : "false";
	}

	return obj;
}

function logUIEvent(name, { type, data } = {}) {
	const t = type || CUSTOM_EVENT_TYPES.navigation;
	const d = Object.keys(data || {})
		.reduce((memo, key) => {
			return {
				...memo,
				[processName(key)]: processName(data[key]),
			};
		}, {});
	let platform = "desktop";

	if (process.env.PLATFORM === "mobile") {
		if (!isApp()) {
			platform = "mobile web";
		} else if (typeof window.APP_PLATFORM !== "undefined") {
			platform = window.APP_PLATFORM;
		} else if (window.App.ios) {
			platform = "ios";
		} else if (window.App.android) {
			platform = "android";
		}
	}

	return _callSoon(_funcs.logEvent, [processName(name), t, {
		...d,
		"debug_platform": platform,
	}]);
}

function setUserProperty(name, value) {
	if (value.constructor === Array) {
		_callSoon(_funcs.setUserAttributeList, [name, value]);
	} else {
		_callSoon(_funcs.setUserAttribute, [name, value]);
	}
}

function setUserProperties(fields) {
	Object.keys(fields).forEach((field) => {
		setUserProperty(field, fields[field]);
	});
}

function removeUserProperty(name) {
	_callSoon(_funcs.removeUserAttribute, [name]);
}

const arrayGoofUp = /\.[0-9]+$/;
const operationGoofUp = /^\$/;
export function isInvalidName(name) {
	if (name !== name.toLowerCase()) {
		return true;
	}

	if (arrayGoofUp.test(name)) {
		return true;
	}

	if (operationGoofUp.test(name)) {
		return true;
	}

	return false;
}

// return what mparticle thinks our userid is
function getMparticleUserid() {
	const user = window.mParticle.Identity.getCurrentUser();
	let mparticleUserid;

	if (user && user.getUserIdentities) {
		const userIdentities = user.getUserIdentities();

		if (userIdentities && userIdentities.userIdentities) {
			mparticleUserid = userIdentities.userIdentities.customerid;
		}
	}

	return mparticleUserid;
}

function loadUserProperties(userid, attributes) {
	if (
		userid && userid !== "null" &&
		window.mParticle &&
		window.mParticle.Identity &&
		getMparticleUserid() !== userid
	) {
		_callSoon(_funcs.identify, {
			userIdentities: {
				customerid: userid,
			},
		});
	} else if (!window.mParticle) {
		ClientStats.update("mParticle window object missing");
	}

	// do a little fucking surgery on mparticle shits
	_callSoon(_funcs.getAllUserAttributes, []).then((currentAttrMap) => {
		if (currentAttrMap && Object.keys(currentAttrMap).length) {
			Object.keys(currentAttrMap).forEach((name) => {
				if (isInvalidName(name)) {
					removeUserProperty(name);
				}
			});
		}

		if (attributes && Object.keys(attributes).length) {
			Object.keys(attributes).forEach((name) => {
				const value = attributes[name];

				// scrub blank values
				if (
					value === null ||
					value === "" ||
					(Array.isArray(value) && !value.length)
				) {
					removeUserProperty(name);
				} else if (!isInvalidName(name)) {
					setUserProperty(name, value);
				}
			});

			// mark these as shown
			// (but not on the app, that does it itself)
			if (!isApp()) {
				OkC.post("/stats/userattributes/updated").catch(() => {});
			}
		}
	});
}

// Library for throwing common alist promo shit
// (This is done in here because it needs to reference this file)
// (And if you do that in another file then import this here, you get a circular reference)
const Promos = {
	trackLikesYouUpsellScreen(likes) {
		logUIEvent(
			EVENT_NAMES.promoAlistLikesYouUpsellScreen,
			{
				data: {
					"Cumulative number of Likes": likes,
				},
			},
		);
	},

	trackLikesYouUpsellHeaderPromo(likes) {
		logUIEvent(
			EVENT_NAMES.promoAlistLikesYouUpsellHeaderPromo,
			{
				data: {
					"Cumulative number of Likes": likes,
				},
			},
		);
	},

	trackMobileInlinePromo({ product, page, feature }) {
		logUIEvent(
			EVENT_NAMES.promoMobileInlinePromo,
			{
				data: {
					"Product": product,
					"Feature": feature,
					"Page": page,
				},
			},
		);
	},

	trackSearchFilterInterstitial(action) {
		logUIEvent(
			EVENT_NAMES.promoAlistSearchFilterInterstitial,
			{
				data: {
					"Option selected": action,
				},
			},
		);
	},


	trackUserRowSidebarLikePromos(page) {
		logUIEvent(
			EVENT_NAMES.promoAlistYellowLikesUpsell,
			{
				data: {
					"Location": page,
					"Advertised Item": "AList Basic",
				},
			},
		);
	},

	trackUserRowSidebarBrowseInvisiblyPromos(page) {
		logUIEvent(
			EVENT_NAMES.promoAlistBrowseInvisiblyUpsell,
			{
				data: {
					"Location": page,
					"Advertised Item": "AList Basic",
				},
			},
		);
	},

	trackSettingsPrivacyBrowseInvisibly() {
		logUIEvent(EVENT_NAMES.promoAlistSettingsBrowseInvisibly);
	},

	trackSettingsGetAlist() {
		logUIEvent(EVENT_NAMES.promoAlistGetAlistSettings);
	},

	trackSettingsPrivacyIncognito() {
		logUIEvent(EVENT_NAMES.promoAlistSettingsIncognito);
	},

	trackSettingsSubscriptionsAlist() {
		logUIEvent(EVENT_NAMES.promoAlistSettingsSubscriptions);
	},

	trackSettingsSubscriptionsIncognito() {
		logUIEvent(EVENT_NAMES.promoIncognitoSettingsSubscriptions);
	},

	trackSettingsSubscriptionsBoostSubs() {
		logUIEvent(EVENT_NAMES.promoBoostSubsSettingsSubscriptions);
	},

	trackBoostMobileProfile(boosts) {
		logUIEvent(
			EVENT_NAMES.promoBoostMobileProfile,
			{
				data: {
					"boost tokens remaining": boosts,
				},
			},
		);

		if (boosts) {
			logUIEvent(
				EVENT_NAMES.boosted,
				{
					data: {
						"source": "my profile",
						"boost tokens remaining": boosts - 1,
					},
				},
			);
		}
	},

	trackMessageClick(params, cb) {
		const data = {
			"match %": params.match_percent,
			"Inbox size": params.inbox_length,
			"conversation age (in days)": params.age,
			"conversation status": params.is_unread ? "unread" : "read",
		};

		logUIEvent(EVENT_NAMES.messagesSelectedInboxMessage, { data });

		if (cb) {
			cb();
		}
	},
};

// public interface
// ----------------------------------------------------------------------------

const Analytics = {
	/**
	 * Log a UI event.
	 * @param {object} name - A unique name for the event.
	 * @param {object} params - Additional params.
	 * @param {object} params.type - Default is Analytics.eventTypes.navigation.
	 * @param {object} params.data - Optional metadata for the event.
	 */
	logUIEvent,

	/**
	 * Set a user property.
	 * @param {string} name - The name of the user property.
	 * @param {(string|string[])} value - A single value or array of values.
	 */
	setUserProperty,

	/**
	 * Parse and set a map that contains user properties.
	 * utilizes the setUserProperty funciton
	 * @param {object} fields - A map of keys (field names) to values
	 */
	setUserProperties,

	/**
	 * Remove a user property.
	 * @param {string} name - The name of the user property.
	 */
	removeUserProperty,

	/**
	 * Load multiple user properties.
	 * @param {string} userid - The userid of the user.
	 * @param {object} attributes - A map of keys (attribute names) to values.
	 */
	loadUserProperties,

	/**
	 * Log into mparticle if their concept of the current user doesn't match our
	 * current userid
	 * @param {string} userid - the userid of the current logged in user
	 */
	logInIfNeeded(userid) {
		const mparticleUserid = getMparticleUserid();
		const platform = window.Context.get("platform");
		let neededLogin = false;

		if (
			mparticleUserid !== userid &&
			window.mParticle.Identity && window.mParticle.Identity.login
		) {
			neededLogin = true;
			window.mParticleIsChangingSession = true;
			window.mParticle.Identity.login({
				userIdentities: {
					customerid: userid,
				},
			}, onChangeSession);
		}

		if (window.Context.get("debugAnalytics")) {
			console.log(
				`ANALYTICS logInIfNeeded for userid ${userid},`,
				`needed login: ${neededLogin}`,
			);
		}

		if (!neededLogin) {
			return;
		}

		// set a fallback for the mobile apps, where callbacks don't fire
		if (platform === "IOS_APP" || platform === "ANDROID_APP") {
			setTimeout(() => {
				if (window.mParticleIsChangingSession) {
					onChangeSession();
				}
			}, 1500);
		}
	},

	/**
	 * Log out of an mparticle session, if one exists.
	 */
	logOutIfNeeded() {
		const mparticleUserid = getMparticleUserid();
		const platform = window.Context.get("platform");
		let neededLogout = false;

		if (
			mparticleUserid &&
			window.mParticle &&
			window.mParticle.Identity &&
			window.mParticle.Identity.logout
		) {
			window.mParticleIsChangingSession = true;
			neededLogout = true;
			window.mParticle.Identity.logout(null, onChangeSession);
		}

		if (window.Context.get("debugAnalytics")) {
			console.log(`ANALYTICS logOutIfNeeded, needed logout: ${neededLogout}`);
		}

		if (!neededLogout) {
			return;
		}

		// set a fallback for the mobile apps, where callbacks don't fire
		if (platform === "IOS_APP" || platform === "ANDROID_APP") {
			setTimeout(() => {
				if (window.mParticleIsChangingSession) {
					onChangeSession();
				}
			}, 1500);
		}
	},

	Promos,

	// attach event names and types to this object
	// (since they're only ever used in analytics calls,
	// so why force people to import all three at once)
	eventTypes: CUSTOM_EVENT_TYPES,
	eventNames: EVENT_NAMES,
};

export default Analytics;



// WEBPACK FOOTER //
// ./src/analytics/util/Analytics/index.js